import { Box } from "@mui/material";
import html2pdf from "html2pdf.js";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";
import PostureTrendChart from "../../../../../components/charts/PostureTrendChart";
import { Toast } from "../../../../../components/Toast";
import {
  FILE_EXTENSIONS,
  generateFileName,
} from "../../../../../hooks/useFileNaming";
import {
  createPdfConfig,
  createPdfContainer,
  createPdfHeader,
  measureContentForPdf,
} from "../../utils/commonWidgetUtils";
import WidgetWrapper from "../../WidgetWrapper";

const PostureTrendWidget = forwardRef(({ mainTimePoint }, ref) => {
  const widgetContentRef = useRef(null);

  const handleExportToPdf = useCallback(async (isCombinedExport = false) => {
    if (!widgetContentRef.current) return null;

    try {
      if (isCombinedExport) {
        // For combined export, return just the widget content with header
        const widgetContainer = document.createElement("div");

        // Add widget header
        const widgetHeader = createPdfHeader("Posture Trend Over Time");
        widgetContainer.appendChild(widgetHeader);

        // Clone and style widget content
        const widgetClone = widgetContentRef.current.cloneNode(true);
        widgetClone.style.cssText = `
          background-color: white;
          padding: 24px;
          border: none;
          width: 89%;
          min-height: fit-content;
        `;

        widgetContainer.appendChild(widgetClone);

        return widgetContainer;
      } else {
        // Individual export - using updated approach
        const entirePageElement = await createPdfContainer();

        const header = createPdfHeader("Posture Trend Over Time");
        entirePageElement.appendChild(header);

        // Clone and style widget content
        const widgetClone = widgetContentRef.current.cloneNode(true);
        widgetClone.style.cssText = `
          background-color: white;
          padding: 24px;
          border: none;
          width: 89%;
          min-height: fit-content;
        `;

        entirePageElement.appendChild(widgetClone);

        // Measure content dimensions
        const { contentWidth, contentHeight } = await measureContentForPdf(
          entirePageElement
        );

        // Generate PDF
        const pdfConfig = createPdfConfig(
          generateFileName("Breeze_Posture_Trend", null, FILE_EXTENSIONS.PDF),
          contentWidth,
          contentHeight
        );

        await html2pdf().set(pdfConfig).from(entirePageElement).save();
        Toast.success("Posture trend chart exported successfully");
      }
    } catch (error) {
      Toast.error("Failed to export posture trend chart");
      return null;
    }
  }, []);

  // Expose the export function to parent components
  useImperativeHandle(
    ref,
    () => ({
      exportToPdf: handleExportToPdf,
      widgetId: "postureTrend",
      hasExportCapability: true,
    }),
    [handleExportToPdf]
  );

  return (
    <WidgetWrapper
      title="Posture Trend Over Time"
      handleExportToPdf={() => handleExportToPdf(false)}
    >
      <Box
        ref={widgetContentRef}
        sx={{
          height: "100%",
          width: "100%",
          padding: "16px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <PostureTrendChart height={400} />
      </Box>
    </WidgetWrapper>
  );
});

PostureTrendWidget.displayName = "PostureTrendWidget";

export default PostureTrendWidget;
